import Empty from "assets/icons/Empty";
import { payerTypeConstants } from "constants/payer";
import { TabView, TabPanel } from "primereact/tabview";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { CoverageDetailsLine } from "../../CoverageDetailsLine";
import { ValueNoteSection } from "../../ValueNoteSection";
import { Message } from "./Message";

const StyledBenefitCointainer = styled.div<{
  showAdditionalDetailsSection: boolean;
  $fullWidth?: boolean;
}>`
  border-radius: 9px;
  min-height: ${({ $fullWidth }) => ($fullWidth ? "auto" : "556px")} !important;
  padding: ${({ $fullWidth }) => ($fullWidth ? "0" : "8px")};
  padding-top: 0px;
  justify-content: ${({ showAdditionalDetailsSection }) =>
    showAdditionalDetailsSection ? "flex-start" : "center"};
  display: flex;
  flex-direction: column;
`;

const StyledTabView = styled(TabView)`
  width: 100%;
  .p-tabview-panels {
    border: 1px solid #e2e2e4;
    margin-top: 20px;
    border-radius: 12px;
    padding-top: 0px;
    padding-left: 0px;
    padding-right: 0px;
  }
  .p-tabview-selected .p-tabview-nav-link {
    background-color: #edf1fd !important;
  }
  .p-tabview-nav {
    overflow-x: auto !important;
    white-space: nowrap !important;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  .p-tabview-nav::-webkit-scrollbar {
    height: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  .p-tabview-nav ul {
    flex-wrap: nowrap !important;
  }
`;

const StyledTabPanelheader = styled.div`
  background-color: #f2f4f7;
  color: #344054;
  padding: 12px 24px;
  border-radius: 12px 12px 0px 0px;
  border-bottom: 1px solid #eaecf0;
`;

const StyledVerticalLine = styled.div`
  height: 100%;
  border-color: var(--gray-new-200);
`;

const StyledCoverageStatusCard = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
`;

const StyledStatusBadge = styled.span<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${({ isActive }) => (isActive ? "#dcfce7" : "#fee2e2")};
  color: ${({ isActive }) => (isActive ? "#166534" : "#991b1b")};
`;

// Format a raw pVerify MonetaryAmount string (e.g. "283") as currency ("$283.00").
const formatMonetaryAmount = (amount?: string | null) =>
  amount !== null && amount !== undefined && amount !== ""
    ? `$${parseFloat(amount).toFixed(2)}`
    : null;

export function PatientBenefits({
  benefitDetailsField,
  eligibilityMessage,
  deductibleField,
  outOfPocketMaxField,
  coinsuranceField,
  handleChangeDeductibleField,
  handleChangeOOPField,
  handleChangeCoinsuranceField,
  readOnly = false,
  fullWidth = false,
  payerType,
}: {
  benefitDetailsField: any;
  eligibilityMessage?: string | null;
  deductibleField?: number;
  outOfPocketMaxField?: number;
  coinsuranceField?: number;
  handleChangeDeductibleField?: (value: number) => void;
  handleChangeOOPField?: (value: number) => void;
  handleChangeCoinsuranceField?: (value: number) => void;
  readOnly?: boolean;
  fullWidth?: boolean;
  payerType?: string | null;
}) {
  // Prefer the explicitly passed payerType (CreateOrder flow); fall back to the
  // payerType stored on the benefits response so the saved OrderView dialog,
  // which does not pass the prop, still renders the Medicaid view (CLE-2466).
  const resolvedPayerType =
    payerType ?? benefitDetailsField?.response?.payerType;
  const isMedicaid = resolvedPayerType === payerTypeConstants.MEDICAID;
  console.log("resolvedPayerType", resolvedPayerType);
  const [selectedDeductible, setSelectedDeductible] = useState<string | null>(
    null
  );
  const [selectedOOP, setSelectedOOP] = useState<string | null>(null);
  const [selectedCoinsurance, setSelectedCoinsurance] = useState<string | null>(
    null
  );
  const eligibilityDetailsField = useMemo(() => {
    return benefitDetailsField?.response?.details;
  }, [benefitDetailsField]);

  const payerName = useMemo(() => {
    const payerName = {
      Value: benefitDetailsField?.response?.payerName,
    };
    return [payerName].filter((value) => value !== null && value !== undefined);
  }, [benefitDetailsField]);

  const planName = useMemo(() => {
    const planName = {
      Value: benefitDetailsField?.response?.planCoverageSummary?.PlanName,
    };
    return [planName].filter((value) => value !== null && value !== undefined);
  }, [benefitDetailsField]);

  const effectiveDate = useMemo(() => {
    const effectiveDate = {
      Value: benefitDetailsField?.response?.planCoverageSummary?.EffectiveDate,
    };
    return [effectiveDate].filter(
      (value) => value !== null && value !== undefined
    );
  }, [benefitDetailsField]);

  const policyType = useMemo(() => {
    const policyType = {
      Value: benefitDetailsField?.response?.planCoverageSummary?.PolicyType,
    };
    return [policyType].filter(
      (value) => value !== null && value !== undefined
    );
  }, [benefitDetailsField]);

  const serviceDetails = useMemo(() => {
    const mappedServiceDetails = benefitDetailsField?.response?.serviceDetails
      ?.filter((s: any) => s.ServiceName !== "Health Benefit Plan Coverage")
      .map((s: any) => ({
        ...s,
        items: [
          {
            dates: s.EligibilityDetails?.[0]?.Date,
          },
        ],
      }));
    const filteredServiceDetails = mappedServiceDetails?.filter((s: any) =>
      s.items?.some((item: any) => item.dates?.length > 0)
    );
    return filteredServiceDetails || [];
  }, [benefitDetailsField]);

  console.log("servicedetails", serviceDetails);

  const coverageDetails = useMemo(() => {
    const coverageDetails =
      benefitDetailsField?.response?.serviceDetails?.filter(
        (s: any) => s.ServiceName === "Health Benefit Plan Coverage"
      )?.[0]?.EligibilityDetails;
    if (!coverageDetails) {
      return null;
    }
    const activeAndPrimaryCoverages = coverageDetails?.filter((c: any) => {
      return (
        c?.EligibilityOrBenefit === "Active Coverage" ||
        c?.EligibilityOrBenefit === "Primary Care Provider"
      );
    });
    return activeAndPrimaryCoverages || null;
  }, [benefitDetailsField]);

  // Check if individual deductible tier exists based on calendar year value
  // Only show N/A when calendar year is explicitly 0 (no individual tier in plan)
  // null/undefined means data not available, so check if we have remaining deductible
  const hasIndividualDeductibleTier = useMemo(() => {
    // Check remaining deductible first — a positive remaining value is direct proof the tier exists,
    // regardless of what calendar year says (some plans use Contract period, not Calendar Year)
    const remainingValue =
      benefitDetailsField?.response?.details?.HBPC_Deductible_OOP_Summary
        ?.IndividualDeductibleRemainingInNet?.Value;
    if (remainingValue !== null && remainingValue !== undefined) {
      const remainingNumeric = parseFloat(remainingValue.replace(/[$,]/g, ""));
      if (remainingNumeric > 0) {
        return true;
      }
    }
    // Remaining is absent or $0 — use calendar year to determine if tier exists
    const calendarYearValue =
      benefitDetailsField?.response?.individualCalendarYearDeductible;
    // Calendar year > 0 means tier exists (remaining $0 = deductible is met)
    if (
      calendarYearValue !== null &&
      calendarYearValue !== undefined &&
      calendarYearValue > 0
    ) {
      return true;
    }
    // Calendar year is 0 or null with no positive remaining — no individual tier
    return false;
  }, [benefitDetailsField]);

  const individualDeductible = useMemo(() => {
    // If no individual tier, show N/A
    if (!hasIndividualDeductibleTier) {
      return [{ Value: "N/A", name: "Individual Remaining 0" }];
    }

    const individualDeductibleRemainingInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.IndividualDeductibleRemainingInNet;
    const filtered = [individualDeductibleRemainingInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Individual Remaining ${index}`,
    }));
  }, [eligibilityDetailsField, hasIndividualDeductibleTier]);

  const familyDeductible = useMemo(() => {
    const familyRemainingDeductibleInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.FamilyDeductibleRemainingInNet;
    const filtered = [familyRemainingDeductibleInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Family Remaining ${index}`,
    }));
  }, [eligibilityDetailsField]);

  const individualCalendarYearDeductible = useMemo(() => {
    const calendarYearValue =
      benefitDetailsField?.response?.individualCalendarYearDeductible;
    // Only show N/A when calendar year is explicitly 0 (no individual tier)
    if (calendarYearValue === 0) {
      return [{ Value: "N/A", name: "Individual Calendar Year 0" }];
    }
    // If null/undefined, don't show this field at all (empty array)
    if (calendarYearValue === null || calendarYearValue === undefined) {
      return [];
    }
    return [
      {
        Value: `$${calendarYearValue.toFixed(2)}`,
        name: "Individual Calendar Year 0",
      },
    ];
  }, [benefitDetailsField]);

  const familyCalendarYearDeductible = useMemo(() => {
    const value = benefitDetailsField?.response?.familyCalendarYearDeductible;
    if (value === null || value === undefined) return [];
    return [{ Value: `$${value.toFixed(2)}`, name: "Family Calendar Year 0" }];
  }, [benefitDetailsField]);

  // For Medicaid payers we never show the Medicare Part A deductible (e.g. $1,736) that
  // pVerify can return for dual-eligible plans — only the Part B figures. We list every
  // Part B deductible line from ServiceDetails (INDIVIDUAL / FAMILY DEDUCTIBLE and their
  // REMAINING variants, all Part B), plus the plan out-of-pocket lines, as plain display
  // rows (not selectable). The "$0 because Medicaid" note lives in the benefits-form
  // warning. HBPC_Deductible_OOP_Summary is intentionally NOT read for the deductible
  // because it conflates the Part A amount for these plans (CLE-2466).
  const medicaidHbpcServiceDetails = useMemo(
    () =>
      benefitDetailsField?.response?.serviceDetails?.find(
        (s: any) => s.ServiceName === "Health Benefit Plan Coverage"
      )?.EligibilityDetails ?? [],
    [benefitDetailsField]
  );

  const medicaidDeductible = useMemo(() => {
    if (!isMedicaid) return [];
    // Every Medicare Part B "Deductible" line, in the order pVerify returned them.
    const rows = medicaidHbpcServiceDetails
      .filter(
        (d: any) =>
          d.EligibilityOrBenefit === "Deductible" &&
          d.InsuranceType === "Medicare Part B"
      )
      .map((d: any, index: number) => {
        const value = formatMonetaryAmount(d.MonetaryAmount);
        return value
          ? {
              Value: value,
              subtitle: d.PlanCoverageDescription || d.CoverageLevel || "",
              name: `Medicaid Part B Deductible ${index}`,
            }
          : null;
      })
      .filter((row: any): row is NonNullable<typeof row> => row !== null);
    console.log("rows", rows);
    if (rows.length) return rows;
    // Fall back to the explicit MedicareInfoSummary Part B figure (straight Medicare
    // dual-eligibles), otherwise a single $0 line so the section is never empty.
    const explicitPartB =
      eligibilityDetailsField?.MedicareInfoSummary?.Part_B_Deductible_Remaining
        ?.Value;
    return [
      {
        Value: explicitPartB ?? "$0.00",
        subtitle: "Individual Deductible Remaining",
        name: "Medicaid Part B Deductible",
      },
    ];
  }, [isMedicaid, eligibilityDetailsField, medicaidHbpcServiceDetails]);

  const medicaidOOP = useMemo(() => {
    if (!isMedicaid) return [];
    // Every "Out of Pocket" line (these are not split by Medicare part; exclude any
    // Part A row defensively). In the order pVerify returned them.
    const rows = medicaidHbpcServiceDetails
      .filter(
        (d: any) =>
          d.EligibilityOrBenefit === "Out of Pocket" &&
          d.InsuranceType !== "Medicare Part A"
      )
      .map((d: any, index: number) => {
        const value = formatMonetaryAmount(d.MonetaryAmount);
        return value
          ? {
              Value: value,
              subtitle: d.PlanCoverageDescription || d.CoverageLevel || "",
              name: `Medicaid Part B OOP ${index}`,
            }
          : null;
      })
      .filter((row: any): row is NonNullable<typeof row> => row !== null);
    if (rows.length) return rows;
    // Fall back to the HBPC summary remaining OOP, otherwise a single $0 line.
    const summaryOOP =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.IndividualOOPRemainingInNet?.Value;
    return [
      {
        Value: summaryOOP ?? "$0.00",
        subtitle: "Individual Out Of Pocket Remaining",
        name: "Medicaid Part B OOP",
      },
    ];
  }, [isMedicaid, eligibilityDetailsField, medicaidHbpcServiceDetails]);

  const individualOOP = useMemo(() => {
    const individualRemainingOOPInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.IndividualOOPRemainingInNet;
    const filtered = [individualRemainingOOPInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Individual Remaining OOP ${index}`,
    }));
  }, [eligibilityDetailsField]);

  const familyOOP = useMemo(() => {
    const familyRemainingOOPInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.FamilyOOPRemainingInNet;
    const filtered = [familyRemainingOOPInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Family Remaining OOP ${index}`,
    }));
  }, [eligibilityDetailsField]);

  // CLE-2684 — the plan's calendar-year (total annual) OOP, read from the same stored
  // summary as the remaining rows so it also renders for orders created before this
  // change. pVerify names the individual field with an underscore (IndividualOOP_InNet)
  // but the family one without (FamilyOOPInNet).
  const individualCalendarYearOOP = useMemo(() => {
    const individualOOPInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary?.IndividualOOP_InNet;
    const filtered = [individualOOPInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Individual Calendar Year OOP ${index}`,
    }));
  }, [eligibilityDetailsField]);

  const familyCalendarYearOOP = useMemo(() => {
    const familyOOPInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary?.FamilyOOPInNet;
    const filtered = [familyOOPInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Family Calendar Year OOP ${index}`,
    }));
  }, [eligibilityDetailsField]);

  const otherOOPOptions = useMemo(() => {
    const otherOptions =
      eligibilityDetailsField?.otherRemainingOutOfPocketMaxOptions || [];
    return otherOptions.filter(
      (value: any) => value !== null && value !== undefined
    );
  }, [eligibilityDetailsField]);

  // Separate Individual and Family OOP options
  const otherIndividualOOPOptions = useMemo(() => {
    const filtered = otherOOPOptions.filter(
      (option: any) => option.CoverageLevel === "Individual"
    );
    return filtered.map((value: any, index: number) => ({
      ...value,
      name: `Other Individual OOP ${index}`,
    }));
  }, [otherOOPOptions]);

  const otherFamilyOOPOptions = useMemo(() => {
    const filtered = otherOOPOptions.filter(
      (option: any) => option.CoverageLevel === "Family"
    );
    return filtered.map((value: any, index: number) => ({
      ...value,
      name: `Other Family OOP ${index}`,
    }));
  }, [otherOOPOptions]);

  const otherDeductibleOptions = useMemo(() => {
    const otherOptions = eligibilityDetailsField?.otherDeductibleOptions || [];
    return otherOptions.filter(
      (value: any) => value !== null && value !== undefined
    );
  }, [eligibilityDetailsField]);

  // Separate Individual and Family Deductible options
  const otherIndividualDeductibleOptions = useMemo(() => {
    const filtered = otherDeductibleOptions.filter(
      (option: any) => option.CoverageLevel === "Individual"
    );
    return filtered.map((value: any, index: number) => ({
      ...value,
      name: `Other Individual Deductible ${index}`,
    }));
  }, [otherDeductibleOptions]);

  const otherFamilyDeductibleOptions = useMemo(() => {
    const filtered = otherDeductibleOptions.filter(
      (option: any) => option.CoverageLevel === "Family"
    );
    return filtered.map((value: any, index: number) => ({
      ...value,
      name: `Other Family Deductible ${index}`,
    }));
  }, [otherDeductibleOptions]);

  // A Medicare response can report two co-insurance blocks — QMB at 0% while Medicaid
  // covers the cost-share, and standard Part B at 20% for the rest of the year — each with
  // its own plan window. The API defaults to whichever window contains the service date and
  // hands us both so the estimator can override it (CLE-2624).
  const otherCoinsuranceOptions = useMemo(() => {
    const options = eligibilityDetailsField?.otherCoinsuranceOptions || [];
    // The plan window is already carried in `Notes` ("Plan: 04/01/2026 - 12/31/2026"), which
    // BenefitLine renders under the label — do NOT also pass `dates`, or the window is printed
    // twice on every row (once left, once as the right-hand date chip).
    return options
      .filter((option: any) => option !== null && option !== undefined)
      .map((option: any, index: number) => ({
        ...option,
        subtitle: option.PolicyType,
        name: `Other Coinsurance ${index}`,
      }));
  }, [eligibilityDetailsField]);

  const specialitOfficeBenefits = useMemo(() => {
    const specialistOfficeSummaryCoPay =
      eligibilityDetailsField?.SpecialistOfficeSummary?.CoPayInNet;
    const specialistOfficeSummaryCoins =
      eligibilityDetailsField?.SpecialistOfficeSummary?.CoInsInNet;
    const individualDeductible = eligibilityDetailsField
      ?.SpecialistOfficeSummary?.IndividualDeductibleRemainingInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.SpecialistOfficeSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.SpecialistOfficeSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: specialistOfficeSummaryCoPay?.Value,
      Notes: specialistOfficeSummaryCoPay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: specialistOfficeSummaryCoins?.Value,
      Notes: specialistOfficeSummaryCoins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      specialistOfficeSummaryCoPay || copayFromServiceDetails
        ? copayDetails
        : null;
    const item2 =
      specialistOfficeSummaryCoins || coinsuranceFromServiceDetails?.length
        ? coinsDetails
        : null;
    const item3 = individualDeductible
      ? { subtitle: "Remaining Deductible", ...individualDeductible }
      : null;
    return [item1, item2, item3].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const ascSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.ASC_FacilitySummary?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.ASC_FacilitySummary?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copaysFromServiceDetails =
      eligibilityDetailsField?.ASC_FacilitySummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.ASC_FacilitySummary?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const hospitalInpatientSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.HospitalInpatientSummary
      ?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.HospitalInpatientSummary
      ?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.HospitalInpatientSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.HospitalInpatientSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copayFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const hospitalOutpatientBenefits = useMemo(() => {
    const copay = eligibilityDetailsField?.HospitalOutPatientSummary
      ?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.HospitalOutPatientSummary
      ?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.HospitalOutPatientSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.HospitalOutPatientSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };

    const item1 =
      copay || copayFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const mriCtSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.MRI_CT_ScanSummary?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.MRI_CT_ScanSummary?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copaysFromServiceDetails =
      eligibilityDetailsField?.MRI_CT_ScanSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.MRI_CT_ScanSummary?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  /**
   * `Professional (Physician) Visit - Outpatient`, and ONLY that block.
   *
   * It used to mix the Surgical block in — surgeon-message copays plus every Surgical
   * co-insurance line — and it rendered whenever EITHER source had data, so a response carrying
   * no professional-visit block at all still produced this tab, populated purely from Surgical.
   * Once CLE-2675 gave Surgical its own tab that became the same benefit shown twice with
   * different slices, which is exactly what Ani reported on 2026-08-25 (response
   * `f024aece-119c-4f09-8cb7-cece104a2abb`: no professional-visit line, one 0% Surgical
   * co-insurance line, two tabs). One tab per pVerify block now; surgeon copays live under
   * Surgical (Physician Benefit).
   */
  const physicianVisitOutpatientBenefits = useMemo(() => {
    const copay = eligibilityDetailsField?.PhysicianVisitOutpatientSummary
      ?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.PhysicianVisitOutpatientSummary
      ?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copaysFromServiceDetails =
      eligibilityDetailsField?.PhysicianVisitOutpatientSummary?.serviceDetails
        ?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.PhysicianVisitOutpatientSummary?.serviceDetails
        ?.coinsurance;

    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };

    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;

    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const surgicalSummary = useMemo(() => {
    // Only show surgical copays with the specific surgeon message
    const surgicalCopaysFromServiceDetails =
      eligibilityDetailsField?.SurgicalSummary?.serviceDetails?.copay?.filter(
        (copay: any) =>
          copay.messages &&
          copay.messages.some(
            (msg: string) =>
              msg === "COPAYMENT PER EACH PERFORMING SURGEON AND CO-SURGEON" ||
              msg ===
                "IN SETTING OTHER THAN OFFICE; COPAYMENT PER EACH PERFORMING SURGEON AND CO-SURGEON"
          )
      );

    const surgicalCopayDetails = {
      subtitle: "Surgical Copay",
      Value: null,
      Notes: null,
      additionalCopay: surgicalCopaysFromServiceDetails,
    };

    const item1 = surgicalCopaysFromServiceDetails?.length
      ? surgicalCopayDetails
      : null;

    return [item1].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  /**
   * CLE-2675 — the whole `Surgical` block (pVerify service type 2), not just the surgeon copays
   * above.
   *
   * Accounts on the by-payer benefit priority read this block for every non-Medicare-Advantage
   * payer, so it is now the section a commercial estimate's copay and coinsurance come from — and
   * until now it had no tab at all. `surgicalSummary` stays as it is: it is the narrow
   * "per performing surgeon" view, and it feeds a different question.
   *
   * Like the Specialist Office tab, this renders for any account whose payer returned the block —
   * empty tabs are filtered out below.
   */
  const surgicalBenefits = useMemo(() => {
    const copay = eligibilityDetailsField?.SurgicalSummary?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.SurgicalSummary?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.SurgicalSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.SurgicalSummary?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copayFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const xRaySummary = useMemo(() => {
    const copay = eligibilityDetailsField?.Diagnostic_X_Ray?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.Diagnostic_X_Ray?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const item1 = copay ? { subtitle: "Copay", ...copay } : null;
    const item2 = coins ? { subtitle: "Coinsurance", ...coins } : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const showAdditionalDetailsSection = useMemo(() => {
    const hasData =
      individualDeductible.length > 0 ||
      familyDeductible.length > 0 ||
      individualOOP.length > 0 ||
      familyOOP.length > 0 ||
      individualCalendarYearOOP.length > 0 ||
      familyCalendarYearOOP.length > 0 ||
      otherOOPOptions.length > 0 ||
      otherDeductibleOptions.length > 0 ||
      individualCalendarYearDeductible.length > 0 ||
      familyCalendarYearDeductible.length > 0 ||
      specialitOfficeBenefits.length > 0 ||
      ascSummary.length > 0 ||
      hospitalInpatientSummary.length > 0 ||
      mriCtSummary.length > 0 ||
      hospitalOutpatientBenefits.length > 0 ||
      physicianVisitOutpatientBenefits.length > 0 ||
      surgicalBenefits.length > 0 ||
      surgicalSummary.length > 0;
    return eligibilityDetailsField && hasData;
  }, [
    individualDeductible,
    familyDeductible,
    individualOOP,
    familyOOP,
    individualCalendarYearOOP,
    familyCalendarYearOOP,
    otherOOPOptions,
    otherDeductibleOptions,
    individualCalendarYearDeductible,
    familyCalendarYearDeductible,
    eligibilityDetailsField,
    specialitOfficeBenefits,
    ascSummary,
    hospitalInpatientSummary,
    mriCtSummary,
    hospitalOutpatientBenefits,
    physicianVisitOutpatientBenefits,
    surgicalBenefits,
    surgicalSummary,
  ]);

  const serviceCoverage = useMemo(() => {
    return benefitDetailsField?.response?.serviceCoverage || {};
  }, [benefitDetailsField]);

  // Basic plan coverage info for fallback display when no detailed benefits
  const planCoverageInfo = useMemo(() => {
    const summary = benefitDetailsField?.response?.planCoverageSummary;
    if (!summary?.Status) return null;
    return {
      status: summary.Status,
      planName: summary.PlanName || null,
      effectiveDate: summary.EffectiveDate || null,
      policyType: summary.PolicyType || null,
    };
  }, [benefitDetailsField]);

  const tabs = useMemo(() => {
    return [
      {
        title: "Hospital Outpatient",
        items: hospitalOutpatientBenefits,
        serviceCoverage: serviceCoverage?.hospitalOutpatient,
      },
      {
        title: "ASC Facility",
        items: ascSummary,
        serviceCoverage: serviceCoverage?.ascFacility,
      },
      {
        title: "Hospital Inpatient",
        items: hospitalInpatientSummary,
        serviceCoverage: serviceCoverage?.hospitalInpatient,
      },
      // Disabled since the May 2025 redesign with no recorded reason; re-enabled for CLE-2675 —
      // office-based accounts (Vein-ity) run Specialist Office eligibility checks and could not
      // see the block their copay was selected from. Empty tabs are filtered out below, so this
      // renders only when the payer actually returned office-visit data.
      {
        title: "Specialist Office",
        items: specialitOfficeBenefits,
        serviceCoverage: serviceCoverage?.specialistOffice,
      },
      {
        title: "Professional Visit - Outpatient",
        items: physicianVisitOutpatientBenefits,
        serviceCoverage: serviceCoverage?.physicianVisitOutpatient,
      },
      // CLE-2675 — pVerify's `Surgical` block (service type 2). Ani's name, chosen 2026-08-25 to
      // be the ONE word used for this benefit everywhere: the Service Type options, this tab and
      // the account's benefit-priority setting. The tab above is the separate
      // `Professional (Physician) Visit - Outpatient` block, which used to compete for the name.
      {
        title: "Surgical (Physician Benefit)",
        items: surgicalBenefits,
      },
      {
        title: "MRI/CT Scan Benefits",
        items: mriCtSummary,
        serviceCoverage: serviceCoverage?.mriCt,
      },
      {
        title: "Diagnostic X-Ray",
        items: xRaySummary,
      },
    ].filter((tab) => tab.items.length > 0);
  }, [
    hospitalOutpatientBenefits,
    ascSummary,
    hospitalInpatientSummary,
    specialitOfficeBenefits,
    physicianVisitOutpatientBenefits,
    surgicalBenefits,
    mriCtSummary,
    xRaySummary,
    serviceCoverage,
  ]);

  useEffect(() => {
    const deductibleValues = [
      ...(individualDeductible.length ? [individualDeductible[0]] : []),
      ...(individualCalendarYearDeductible.length
        ? [individualCalendarYearDeductible[0]]
        : []),
      ...(familyDeductible.length ? [familyDeductible[0]] : []),
      ...(familyCalendarYearDeductible.length
        ? [familyCalendarYearDeductible[0]]
        : []),
      ...otherIndividualDeductibleOptions,
      ...otherFamilyDeductibleOptions,
    ];
    const formattedDeductible = Number(deductibleField).toFixed(2);
    const matchingField = deductibleValues.find(
      (value) => value.Value === `$${formattedDeductible}`
    );
    if (matchingField) {
      setSelectedDeductible(matchingField.name);
    } else {
      setSelectedDeductible(null);
    }
  }, [
    individualDeductible,
    individualCalendarYearDeductible,
    familyDeductible,
    familyCalendarYearDeductible,
    otherIndividualDeductibleOptions,
    otherFamilyDeductibleOptions,
    deductibleField,
    benefitDetailsField,
  ]);

  useEffect(() => {
    const oopValues = [
      ...individualOOP,
      ...familyOOP,
      ...otherIndividualOOPOptions,
      ...otherFamilyOOPOptions,
    ];
    const matchingField = oopValues.find(
      (value) => value.Value === `$${outOfPocketMaxField}`
    );
    if (matchingField) {
      setSelectedOOP(matchingField.name);
    } else {
      setSelectedOOP(null);
    }
  }, [
    outOfPocketMaxField,
    individualOOP,
    familyOOP,
    otherIndividualOOPOptions,
    otherFamilyOOPOptions,
  ]);

  useEffect(() => {
    const matchingField = otherCoinsuranceOptions.find(
      (option: any) => option.Percent === Number(coinsuranceField)
    );
    setSelectedCoinsurance(matchingField ? matchingField.name : null);
  }, [coinsuranceField, otherCoinsuranceOptions]);

  const handleSelectDeductible = (value: string, name: string) => {
    setSelectedDeductible(name);
    const valueFromString = value.split("$")[1];
    const valueFromNumber = parseFloat(valueFromString);
    handleChangeDeductibleField?.(valueFromNumber);
  };

  const handleSelectOOP = (value: string, name: string) => {
    setSelectedOOP(name);
    const valueFromString = value.split("$")[1];
    const valueFromNumber = parseFloat(valueFromString);
    handleChangeOOPField?.(valueFromNumber);
  };

  const handleSelectCoinsurance = (value: string, name: string) => {
    setSelectedCoinsurance(name);
    // Co-insurance options are percentages ("20%"), not the "$0.00" the other two carry.
    const valueFromNumber = parseFloat(value.replace("%", ""));
    if (!Number.isNaN(valueFromNumber)) {
      handleChangeCoinsuranceField?.(valueFromNumber);
    }
  };

  const showBasicCoverageOnly =
    !showAdditionalDetailsSection && planCoverageInfo;

  return (
    <div
      className={`flex flex-row w-full ${
        fullWidth ? "" : "xl:w-7"
      } align-items-center`}
    >
      {!fullWidth &&
        (eligibilityMessage ||
          showAdditionalDetailsSection ||
          showBasicCoverageOnly) && (
          <StyledVerticalLine className="hidden xl:block border-left-1  mr-4" />
        )}
      <StyledBenefitCointainer
        className="w-full pt-5 mb-4"
        showAdditionalDetailsSection={
          showAdditionalDetailsSection || showBasicCoverageOnly
        }
        $fullWidth={fullWidth}
      >
        {!eligibilityMessage &&
          !showAdditionalDetailsSection &&
          !planCoverageInfo && (
            <div className="flex w-full flex-column align-items-center">
              <Empty width={100} height={100} style={{ color: "#CED4DA" }} />
              <p style={{ color: "#ced4da" }}>
                Please, run eligibility to get information
              </p>
            </div>
          )}
        {showBasicCoverageOnly && (
          <StyledCoverageStatusCard>
            <div className="flex flex-column gap-4">
              <div className="flex align-items-center justify-content-between">
                <span className="text-lg font-semibold text-gray-700">
                  Coverage Status
                </span>
                <StyledStatusBadge
                  isActive={planCoverageInfo.status === "Active"}
                >
                  {planCoverageInfo.status === "Active" ? (
                    <i className="pi pi-check-circle" />
                  ) : (
                    <i className="pi pi-times-circle" />
                  )}
                  {planCoverageInfo.status}
                </StyledStatusBadge>
              </div>
              {planCoverageInfo.planName && (
                <div className="flex flex-column gap-1">
                  <span className="text-sm text-gray-500">Plan Name</span>
                  <span className="text-base font-medium text-gray-800">
                    {planCoverageInfo.planName}
                  </span>
                </div>
              )}
              {planCoverageInfo.effectiveDate && (
                <div className="flex flex-column gap-1">
                  <span className="text-sm text-gray-500">Effective Date</span>
                  <span className="text-base text-gray-700">
                    {planCoverageInfo.effectiveDate}
                  </span>
                </div>
              )}
              {planCoverageInfo.policyType && (
                <div className="flex flex-column gap-1">
                  <span className="text-sm text-gray-500">Policy Type</span>
                  <span className="text-base text-gray-700">
                    {planCoverageInfo.policyType}
                  </span>
                </div>
              )}
              <p className="text-sm text-gray-500 mt-2">
                Detailed benefit information is not available for this plan.
                Please contact the payer for specific coverage details.
              </p>
            </div>
          </StyledCoverageStatusCard>
        )}
        {eligibilityMessage && (
          <Message
            eligibilityMessage={eligibilityMessage}
            showAdditionalDetailsSection={showAdditionalDetailsSection}
          />
        )}
        {showAdditionalDetailsSection && (
          <div className="w-full flex overflow-x-auto">
            <StyledTabView>
              <TabPanel header="Plan Deductible/OOP">
                <StyledTabPanelheader className="flex justify-content-between header">
                  <p className="text-xs">Description</p>
                  <p className="text-xs">Benefit Details</p>
                </StyledTabPanelheader>
                <ValueNoteSection title="Payer Name" items={payerName} />
                <ValueNoteSection title="Plan Name" items={planName} />
                {effectiveDate?.length && (
                  <ValueNoteSection
                    title="Effective Date"
                    items={effectiveDate}
                  />
                )}
                <ValueNoteSection title="Policy Type" items={policyType} />
                {isMedicaid && coverageDetails?.length && (
                  <div className="mt-4">
                    <p className="px-4 mb-3 text-lg font-bold">
                      Health Benefit Plan Coverage Details
                    </p>
                    {coverageDetails.map((d: any, i: number) => (
                      <CoverageDetailsLine
                        key={i}
                        title={`${d.CoverageLevel} ${d.EligibilityOrBenefit}`}
                        planCoverageDescription={d.PlanCoverageDescription}
                        date={d?.Date?.length && d?.Date[0]}
                        benefitEntities={d?.BenefitEntities}
                        insuranceType={d?.InsuranceType}
                      />
                    ))}
                  </div>
                )}
                {isMedicaid && serviceDetails?.length ? (
                  <div className="mt-4">
                    <p className="px-4 mb-3 text-lg font-bold">Services</p>
                    {serviceDetails.map((s: any, i: number) => (
                      <ValueNoteSection
                        key={i}
                        title={s.ServiceName}
                        items={s.items}
                        alignWithRadio
                      />
                    ))}
                  </div>
                ) : null}

                {/* Medicaid: show only Medicare Part B (or $0) with a zeroed-out note */}
                {isMedicaid && (
                  <>
                    <div className="mt-4">
                      <p className="px-4 mb-3 text-lg font-bold">Deductible</p>
                      <ValueNoteSection
                        title="Medicare Part B"
                        items={medicaidDeductible}
                      />
                    </div>
                    <div className="mt-4">
                      <p className="px-4 mb-3 text-lg font-bold">
                        Out of Pocket Max
                      </p>
                      <ValueNoteSection
                        title="Medicare Part B"
                        items={medicaidOOP}
                      />
                    </div>
                  </>
                )}

                {/* Deductible Section */}
                {!isMedicaid &&
                  (individualDeductible.length > 0 ||
                    individualCalendarYearDeductible.length > 0 ||
                    familyDeductible.length > 0 ||
                    familyCalendarYearDeductible.length > 0 ||
                    otherIndividualDeductibleOptions.length > 0 ||
                    otherFamilyDeductibleOptions.length > 0) && (
                    <div className="mt-4">
                      <p className="px-4 mb-3 text-lg font-bold">Deductible</p>
                      <ValueNoteSection
                        title="Individual Remaining"
                        items={individualDeductible}
                        selectable={hasIndividualDeductibleTier && !readOnly}
                        onSelect={
                          hasIndividualDeductibleTier
                            ? handleSelectDeductible
                            : undefined
                        }
                        selected={selectedDeductible}
                        alignWithRadio={
                          !hasIndividualDeductibleTier && !readOnly
                        }
                      />
                      <ValueNoteSection
                        title="Individual Calendar Year"
                        items={individualCalendarYearDeductible}
                        selectable={hasIndividualDeductibleTier && !readOnly}
                        onSelect={
                          hasIndividualDeductibleTier
                            ? handleSelectDeductible
                            : undefined
                        }
                        selected={selectedDeductible}
                        alignWithRadio={
                          !hasIndividualDeductibleTier && !readOnly
                        }
                      />
                      <ValueNoteSection
                        title="Family Remaining"
                        items={familyDeductible}
                        selectable={!readOnly}
                        onSelect={handleSelectDeductible}
                        selected={selectedDeductible}
                      />
                      <ValueNoteSection
                        title="Family Calendar Year"
                        items={familyCalendarYearDeductible}
                        selectable={!readOnly}
                        onSelect={handleSelectDeductible}
                        selected={selectedDeductible}
                      />
                      {otherIndividualDeductibleOptions.length > 0 && (
                        <ValueNoteSection
                          title="Individual Remaining"
                          items={otherIndividualDeductibleOptions}
                          selectable={!readOnly}
                          onSelect={handleSelectDeductible}
                          selected={selectedDeductible}
                        />
                      )}
                      {otherFamilyDeductibleOptions.length > 0 && (
                        <ValueNoteSection
                          title="Family Remaining"
                          items={otherFamilyDeductibleOptions}
                          selectable={!readOnly}
                          onSelect={handleSelectDeductible}
                          selected={selectedDeductible}
                        />
                      )}
                    </div>
                  )}

                {/* Out of Pocket Max Section */}
                {!isMedicaid &&
                  (individualOOP.length > 0 ||
                    familyOOP.length > 0 ||
                    individualCalendarYearOOP.length > 0 ||
                    familyCalendarYearOOP.length > 0 ||
                    otherIndividualOOPOptions.length > 0 ||
                    otherFamilyOOPOptions.length > 0) && (
                    <div className="mt-4">
                      <p className="px-4 mb-3 text-lg font-bold">
                        Out of Pocket Max
                      </p>
                      <ValueNoteSection
                        title="Individual Remaining"
                        items={individualOOP}
                        selectable={!readOnly}
                        onSelect={handleSelectOOP}
                        selected={selectedOOP}
                      />
                      {otherIndividualOOPOptions.length > 0 && (
                        <ValueNoteSection
                          title="Individual Remaining"
                          items={otherIndividualOOPOptions}
                          selectable={!readOnly}
                          onSelect={handleSelectOOP}
                          selected={selectedOOP}
                        />
                      )}
                      {/* CLE-2684 — plan totals are reference data, never selectable:
                          picking one would overwrite the *remaining* OOP pricing input */}
                      <ValueNoteSection
                        title="Individual Calendar Year"
                        items={individualCalendarYearOOP}
                        alignWithRadio={!readOnly}
                      />
                      <ValueNoteSection
                        title="Family Remaining"
                        items={familyOOP}
                        selectable={!readOnly}
                        onSelect={handleSelectOOP}
                        selected={selectedOOP}
                      />
                      {otherFamilyOOPOptions.length > 0 && (
                        <ValueNoteSection
                          title="Family Remaining"
                          items={otherFamilyOOPOptions}
                          selectable={!readOnly}
                          onSelect={handleSelectOOP}
                          selected={selectedOOP}
                        />
                      )}
                      <ValueNoteSection
                        title="Family Calendar Year"
                        items={familyCalendarYearOOP}
                        alignWithRadio={!readOnly}
                      />
                    </div>
                  )}

                {/* Coinsurance Section — only rendered when the payer reported more
                    than one co-insurance block to choose between (CLE-2624) */}
                {otherCoinsuranceOptions.length > 0 && (
                  <div className="mt-4">
                    <p className="px-4 mb-3 text-lg font-bold">Coinsurance</p>
                    <ValueNoteSection
                      title="Coinsurance"
                      items={otherCoinsuranceOptions}
                      selectable={!readOnly}
                      onSelect={handleSelectCoinsurance}
                      selected={selectedCoinsurance}
                    />
                  </div>
                )}
              </TabPanel>
              {tabs.map((tab: any) => {
                return (
                  <TabPanel key={tab.title} header={tab.title}>
                    <ValueNoteSection
                      title={tab.title}
                      items={tab.items}
                      serviceCoverage={tab.serviceCoverage}
                    />
                  </TabPanel>
                );
              })}
            </StyledTabView>
          </div>
        )}
      </StyledBenefitCointainer>
    </div>
  );
}
